import React from "react";

const Step5ReviewSubmit = ({
  formData,
  filePreviews,
  goToStep,
  prevStep,
  onSubmit,
  loading,
}) => {
  const formatCurrency = (value) => {
    if (!value) return "Not specified";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not specified";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getCampaignTypeName = (typeId) => {
    return typeId === "1" ? "Equity" : typeId === "2" ? "Debt" : typeId === "3" ? "Fund" : "Unknown";
  };

  const getCampaignAccessName = (campaignFor) => {
    if (!campaignFor) return "Not specified";
    if (campaignFor === "Accredited Investor") return "Deal Room (Accredited Investor)";
    return campaignFor;
  };

  const formatRiskProfile = (value) => {
    if (!value) return "Not specified";
    return String(value).replace(/_/g, " ");
  };

  return (
    <div className="step-form">
      <div className="form-section">
        <h3 className="section-title">Review Campaign Details</h3>
        <p style={{ color: "#666", marginBottom: "30px" }}>
          Please review all information before submitting. Click on any section to edit.
        </p>

        {/* User Information */}
        <div className="review-section">
          <div className="review-title">
            <span>User Information</span>
            <button className="edit-btn" onClick={() => goToStep(0)}>
              Edit
            </button>
          </div>
          <div className="review-grid">
            <div className="review-item">
              <div className="review-label">User Type</div>
              <div className="review-value">
                {formData.isNewUser ? "New User (will be created)" : "Existing User"}
              </div>
            </div>
            <div className="review-item">
              <div className="review-label">Email</div>
              <div className="review-value">{formData.user_email || "Not specified"}</div>
            </div>
            <div className="review-item">
              <div className="review-label">Name</div>
              <div className="review-value">
                {formData.first_name && formData.last_name
                  ? `${formData.first_name} ${formData.last_name}`
                  : "Not specified"}
              </div>
            </div>
          </div>
        </div>

        {/* Business Information */}
        <div className="review-section">
          <div className="review-title">
            <span>Business Information</span>
            <button className="edit-btn" onClick={() => goToStep(1)}>
              Edit
            </button>
          </div>
          <div className="review-grid">
            <div className="review-item">
              <div className="review-label">Company Name</div>
              <div className="review-value">
                {formData.corporation_legal_name || "Not specified"}
              </div>
            </div>
            <div className="review-item">
              <div className="review-label">Business Category</div>
              <div className="review-value">
                {formData.business_category || "Not specified"}
              </div>
            </div>
            <div className="review-item">
              <div className="review-label">Website</div>
              <div className={`review-value ${!formData.website ? "empty" : ""}`}>
                {formData.website || "Not specified"}
              </div>
            </div>
            <div className="review-item">
              <div className="review-label">Location</div>
              <div className="review-value">
                {formData.city ? `${formData.city}` : "Not specified"}
              </div>
            </div>
          </div>
        </div>

        {/* Campaign Details */}
        <div className="review-section">
          <div className="review-title">
            <span>Campaign Details</span>
            <button className="edit-btn" onClick={() => goToStep(2)}>
              Edit
            </button>
          </div>
          <div className="review-grid">
            <div className="review-item">
              <div className="review-label">
                {formData.campaign_type === "3" ? "Fund Name" : "Campaign Title"}
              </div>
              <div className="review-value">{formData.campaign_title || "Not specified"}</div>
            </div>
            <div className="review-item">
              <div className="review-label">Campaign Type</div>
              <div className="review-value">{getCampaignTypeName(formData.campaign_type)}</div>
            </div>
            <div className="review-item">
              <div className="review-label">Campaign Access</div>
              <div className="review-value">
                {getCampaignAccessName(formData.campaign_for)}
              </div>
            </div>
            <div className="review-item">
              <div className="review-label">Raise Goal</div>
              <div className="review-value">{formatCurrency(formData.raise_goal)}</div>
            </div>
            <div className="review-item">
              <div className="review-label">Minimum Investment</div>
              <div className="review-value">{formatCurrency(formData.minimum_investment)}</div>
            </div>
            {formData.campaign_type === "3" && (
              <>
                <div className="review-item">
                  <div className="review-label">Fund Type</div>
                  <div className="review-value">
                    {formData.fund_type_description || "Not specified"}
                  </div>
                </div>
                <div className="review-item">
                  <div className="review-label">One-Line Description</div>
                  <div className="review-value">
                    {formData.one_line_description || "Not specified"}
                  </div>
                </div>
                <div className="review-item">
                  <div className="review-label">Risk Profile</div>
                  <div className="review-value">
                    {formatRiskProfile(formData.risk_profile)}
                  </div>
                </div>
                <div className="review-item">
                  <div className="review-label">Investment Horizon</div>
                  <div className="review-value">
                    {formData.investment_horizon || "Not specified"}
                  </div>
                </div>
                <div className="review-item">
                  <div className="review-label">Target ARR</div>
                  <div className="review-value">{formData.target_arr || "Not specified"}</div>
                </div>
              </>
            )}
            {formData.campaign_type === "1" && (
              <div className="review-item">
                <div className="review-label">Equity Offered</div>
                <div className="review-value">{formData.equity_percentage}%</div>
              </div>
            )}
            {formData.campaign_type === "2" && (
              <>
                <div className="review-item">
                  <div className="review-label">Interest Rate</div>
                  <div className="review-value">{formData.interest_rate}% per annum</div>
                </div>
                <div className="review-item">
                  <div className="review-label">Maturity Period</div>
                  <div className="review-value">{formData.maturity_months} months</div>
                </div>
              </>
            )}
            <div className="review-item">
              <div className="review-label">Start Date</div>
              <div className="review-value">{formatDate(formData.start_date)}</div>
            </div>
            <div className="review-item">
              <div className="review-label">End Date</div>
              <div className="review-value">{formatDate(formData.end_date)}</div>
            </div>
            <div className="review-item">
              <div className="review-label">Accredited Only</div>
              <div className="review-value">
                {formData.campaign_for === "Accredited Investor" ||
                parseFloat(formData.minimum_investment) > 25000
                  ? "Yes"
                  : "No"}
              </div>
            </div>
          </div>
          <div style={{ marginTop: "15px" }}>
            <div className="review-label">
              {formData.campaign_type === "3" ? "Fund Summary" : "Campaign Description"}
            </div>
            <div
              className="review-value"
              style={{
                marginTop: "8px",
                padding: "15px",
                backgroundColor: "#f8f9fa",
                borderRadius: "6px",
                whiteSpace: "pre-wrap",
              }}
            >
              {formData.campaign_type === "3"
                ? formData.fund_summary || formData.campaign_description || "Not specified"
                : formData.campaign_description || "Not specified"}
            </div>
          </div>

          {formData.campaign_type === "3" && (
            <div style={{ marginTop: "15px" }}>
              <div className="review-label">Key Reasons to Invest</div>
              <div
                className="review-value"
                style={{
                  marginTop: "8px",
                  padding: "15px",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "6px",
                  whiteSpace: "pre-wrap",
                }}
              >
                {formData.key_reasons_to_invest || "Not specified"}
              </div>
            </div>
          )}

          {formData.campaign_type === "3" && (
            <div style={{ marginTop: "15px" }}>
              <div className="review-label">Company Overview</div>
              <div
                className="review-value"
                style={{
                  marginTop: "8px",
                  padding: "15px",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "6px",
                  whiteSpace: "pre-wrap",
                }}
              >
                {formData.company_overview || "Not specified"}
              </div>
            </div>
          )}
        </div>

        {/* Financial Information */}
        <div className="review-section">
          <div className="review-title">
            <span>Financial Information</span>
            <button className="edit-btn" onClick={() => goToStep(3)}>
              Edit
            </button>
          </div>
          <div className="review-grid">
            <div className="review-item">
              <div className="review-label">Target Raise</div>
              <div className="review-value">{formatCurrency(formData.target_raise)}</div>
            </div>
            <div className="review-item">
              <div className="review-label">Company Valuation</div>
              <div className={`review-value ${!formData.valuation ? "empty" : ""}`}>
                {formatCurrency(formData.valuation)}
              </div>
            </div>
            <div className="review-item">
              <div className="review-label">Revenue (Last Year)</div>
              <div className={`review-value ${!formData.revenue_last_year ? "empty" : ""}`}>
                {formatCurrency(formData.revenue_last_year)}
              </div>
            </div>
            <div className="review-item">
              <div className="review-label">Employees</div>
              <div className={`review-value ${!formData.number_of_employees ? "empty" : ""}`}>
                {formData.number_of_employees || "Not specified"}
              </div>
            </div>
            <div className="review-item">
              <div className="review-label">Year Founded</div>
              <div className={`review-value ${!formData.year_founded ? "empty" : ""}`}>
                {formData.year_founded || "Not specified"}
              </div>
            </div>
          </div>
          {/* Banking Info */}
          {(formData.bank_name || formData.account_number) && (
            <div style={{ marginTop: "20px" }}>
              <div className="review-label" style={{ fontWeight: "600", marginBottom: "10px" }}>
                Banking Details
              </div>
              <div className="review-grid">
                <div className="review-item">
                  <div className="review-label">Bank Name</div>
                  <div className={`review-value ${!formData.bank_name ? "empty" : ""}`}>
                    {formData.bank_name || "Not specified"}
                  </div>
                </div>
                <div className="review-item">
                  <div className="review-label">Account Name</div>
                  <div className={`review-value ${!formData.account_name ? "empty" : ""}`}>
                    {formData.account_name || "Not specified"}
                  </div>
                </div>
                <div className="review-item">
                  <div className="review-label">Account Number</div>
                  <div className={`review-value ${!formData.account_number ? "empty" : ""}`}>
                    {formData.account_number ? `****${formData.account_number.slice(-4)}` : "Not specified"}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Files & Media */}
        <div className="review-section">
          <div className="review-title">
            <span>Files & Media</span>
            <button className="edit-btn" onClick={() => goToStep(4)}>
              Edit
            </button>
          </div>
          <div className="review-grid">
            <div className="review-item">
              <div className="review-label">Company Logo</div>
              <div className={`review-value ${!formData.logo ? "empty" : ""}`}>
                {formData.logo ? (
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    {filePreviews.logo && (
                      <img
                        src={filePreviews.logo}
                        alt="Logo"
                        style={{
                          width: "50px",
                          height: "50px",
                          objectFit: "cover",
                          borderRadius: "4px",
                        }}
                      />
                    )}
                    <span>{formData.logo.name}</span>
                  </div>
                ) : (
                  "Not uploaded"
                )}
              </div>
            </div>
            <div className="review-item">
              <div className="review-label">Campaign Banner</div>
              <div className={`review-value ${!formData.banner ? "empty" : ""}`}>
                {formData.banner ? formData.banner.name : "Not uploaded"}
              </div>
            </div>
            <div className="review-item">
              <div className="review-label">Business Plan</div>
              <div className={`review-value ${!formData.business_plan ? "empty" : ""}`}>
                {formData.business_plan ? formData.business_plan.name : "Not uploaded"}
              </div>
            </div>
          </div>
        </div>

        {/* Important Notice */}
        <div
          style={{
            backgroundColor: "#fff3e0",
            border: "1px solid #ffb74d",
            borderRadius: "8px",
            padding: "20px",
            marginTop: "30px",
          }}
        >
          <h4 style={{ color: "#e65100", marginBottom: "10px" }}>Important Notice</h4>
          <ul style={{ margin: 0, paddingLeft: "20px", color: "#666" }}>
            <li>This campaign will be created with status "Active" and immediately published.</li>
            <li>The user will bypass KYC verification requirements.</li>
            <li>
              The campaign will be marked as "created_by_admin" for audit purposes.
            </li>
            <li>
              {formData.isNewUser
                ? "A new user account will be created with the provided email."
                : "The campaign will be associated with the selected existing user."}
            </li>
          </ul>
        </div>
      </div>

      <div className="step-buttons">
        <button className="btn btn-secondary" onClick={prevStep}>
          Back
        </button>
        <button className="btn btn-success" onClick={onSubmit} disabled={loading}>
          {loading ? "Creating Campaign..." : "Create Campaign"}
        </button>
      </div>
    </div>
  );
};

export default Step5ReviewSubmit;
